import { useState } from "react";
import { fetchEnquiries, updateEnquiryStatus } from "@/lib/api";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";
import { MessageCircle, Mail, Copy, Sparkles } from "lucide-react";

const STATUSES = ["New Lead", "Contacted", "Interested", "Quotation Sent", "Follow-up", "Confirmed", "Checked Out"];
const TYPES = [
  { k: "", label: "All" },
  { k: "stay", label: "Stay" },
  { k: "chardham", label: "Char Dham" },
  { k: "wedding", label: "Wedding" },
  { k: "contact", label: "Contact" },
];

// ============ REVIEW REQUEST TEMPLATES ============
const REVIEW_MESSAGE = (guestName) =>
  `Namaste ${guestName || "ji"} 🙏

It was our honour to host you at THE KC INDIA.

If your stay felt warm and memorable, we would be truly grateful if you could share a small blessing on Google — it helps other pilgrim families find us with confidence.

Your review link 🌸
${BRAND.googleMaps}

We look forward to welcoming you back to the Himalayas.

Warm regards,
THE KC INDIA — Kedarnath Cottage
${BRAND.phone}`;

const REVIEW_EMAIL_SUBJECT = "A small blessing for THE KC INDIA 🙏";

const openWhatsApp = (mobile, guestName) => {
  const digits = String(mobile || "").replace(/\D/g, "");
  const wa = digits.startsWith("91") ? digits : `91${digits}`;
  const url = `https://wa.me/${wa}?text=${encodeURIComponent(REVIEW_MESSAGE(guestName))}`;
  window.open(url, "_blank", "noreferrer");
};

const openEmail = (email, guestName) => {
  const url = `mailto:${email}?subject=${encodeURIComponent(REVIEW_EMAIL_SUBJECT)}&body=${encodeURIComponent(REVIEW_MESSAGE(guestName))}`;
  window.location.href = url;
};

const copyMessage = async (guestName) => {
  try {
    await navigator.clipboard.writeText(REVIEW_MESSAGE(guestName));
    toast.success("Review message copied to clipboard");
  } catch {
    toast.error("Could not copy — please copy manually");
  }
};

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("kc_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [rows, setRows] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (t = token, ty = typeFilter) => {
    setLoading(true);
    try {
      const data = await fetchEnquiries(t, ty || undefined);
      setRows(data);
      setAuthed(true);
      localStorage.setItem("kc_admin_token", t);
    } catch (e) {
      toast.error("Unauthorized or unable to fetch. Check the admin token.");
    } finally { setLoading(false); }
  };

  const changeStatus = async (id, status) => {
    try {
      await updateEnquiryStatus(token, id, status);
      toast.success("Updated.");
      setRows(r => r.map(x => x.id === id ? { ...x, status } : x));
    } catch { toast.error("Could not update."); }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center px-6" data-testid="admin-login">
        <div className="kc-card p-10 w-full max-w-md">
          <div className="overline">Admin</div>
          <h1 className="font-serif-display text-3xl mt-2">KC Enquiries Dashboard</h1>
          <p className="text-ink-soft text-sm mt-2">Enter the admin token to continue.</p>
          <input
            type="password"
            value={token}
            onChange={e => setToken(e.target.value)}
            className="w-full mt-6 border border-kc rounded-sm px-4 py-3 focus:outline-none focus:border-saffron"
            placeholder="Admin token"
            data-testid="admin-token-input"
          />
          <button className="btn-saffron mt-4 w-full justify-center" onClick={() => load()} data-testid="admin-login-btn">Enter</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="admin-dashboard">
      <header className="border-b border-kc bg-white/70 sticky top-0 z-20 backdrop-blur-xl">
        <div className="max-w-[1500px] mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-serif-display text-2xl">THE KC INDIA · CRM</div>
            <div className="text-xs text-ink-soft">Enquiries dashboard</div>
          </div>
          <button className="btn-ghost text-xs" onClick={() => { setAuthed(false); localStorage.removeItem("kc_admin_token"); }} data-testid="admin-logout">Logout</button>
        </div>
      </header>

      <div className="max-w-[1500px] mx-auto px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-6">
          {TYPES.map(t => (
            <button
              key={t.k}
              onClick={() => { setTypeFilter(t.k); load(token, t.k); }}
              className={`px-4 py-2 rounded-full text-[13px] border ${typeFilter === t.k ? "bg-pine text-[#F7F5F0] border-pine" : "border-kc"}`}
              data-testid={`admin-filter-${t.k || "all"}`}
            >{t.label}</button>
          ))}
          <button className="ml-auto btn-ghost text-xs" onClick={() => load()} data-testid="admin-refresh">Refresh</button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {["stay", "chardham", "wedding", "contact"].map(t => (
            <div key={t} className="kc-card p-4">
              <div className="overline capitalize">{t}</div>
              <div className="font-serif-display text-3xl mt-1">{rows.filter(r => r.type === t).length}</div>
            </div>
          ))}
        </div>

        <div className="kc-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-table">
            <thead className="bg-[#EEEAE0]">
              <tr>
                {["Type", "Name", "Mobile", "Details", "Status", "Review Request", "Created"].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-medium text-ink-soft">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-kc" data-testid={`admin-row-${r.id}`}>
                  <td className="px-4 py-3 uppercase text-xs text-saffron tracking-widest">{r.type}</td>
                  <td className="px-4 py-3 font-medium">{r.name}</td>
                  <td className="px-4 py-3"><a href={`tel:${r.mobile}`} className="underline">{r.mobile}</a></td>
                  <td className="px-4 py-3 text-ink-soft text-xs max-w-[300px]">
                    {r.destination && (
                      <div className="text-gold text-[10px] uppercase tracking-[0.2em] mb-1">
                        {r.destination}{r.hotel ? ` · ${r.hotel}` : ""}
                      </div>
                    )}
                    {r.type === "stay" && `${r.check_in || "?"} → ${r.check_out || "?"} · ${r.guests || ""} guests · ${r.room_type || ""}`}
                    {r.type === "chardham" && `${r.travel_month || ""} · ${r.guests || ""} guests · ${r.package || ""}`}
                    {r.type === "wedding" && `${r.wedding_month || ""} · ${r.expected_guests || ""} guests · ${r.city || ""}`}
                    {r.type === "contact" && (r.message || "").slice(0, 90)}
                  </td>
                  <td className="px-4 py-3">
                    <select value={r.status} onChange={e => changeStatus(r.id, e.target.value)} className="border border-kc rounded-sm px-2 py-1 text-xs" data-testid={`admin-status-${r.id}`}>
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <ReviewRequestButtons row={r} />
                  </td>
                  <td className="px-4 py-3 text-xs text-ink-soft">{new Date(r.created_at).toLocaleString()}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="text-center px-4 py-16 text-ink-soft">{loading ? "Loading…" : "No enquiries yet."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============ REVIEW REQUEST BUTTONS ============
const ReviewRequestButtons = ({ row }) => {
  const isCheckedOut = row.status === "Checked Out";
  const suggested = isCheckedOut;

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`review-request-${row.id}`}>
      <button
        onClick={() => openWhatsApp(row.mobile, row.name)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-[0.16em] border transition-colors ${
          suggested
            ? "bg-gold/10 border-gold text-gold hover:bg-gold hover:text-[#0F0F0F]"
            : "border-kc text-ink-soft hover:border-gold hover:text-gold"
        }`}
        title="Send review request via WhatsApp"
        data-testid={`req-whatsapp-${row.id}`}
      >
        <MessageCircle size={11} /> WhatsApp
        {suggested && <Sparkles size={9} className="text-gold" />}
      </button>
      {row.email && (
        <button
          onClick={() => openEmail(row.email, row.name)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-kc text-ink-soft hover:border-gold hover:text-gold transition-colors"
          title="Send review request via Email"
          data-testid={`req-email-${row.id}`}
        >
          <Mail size={11} /> Email
        </button>
      )}
      <button
        onClick={() => copyMessage(row.name)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-kc text-ink-soft hover:border-gold hover:text-gold transition-colors"
        title="Copy the review request message"
        data-testid={`req-copy-${row.id}`}
      >
        <Copy size={11} />
      </button>
    </div>
  );
};
